import { observer } from 'owa-mobx-react';
import { MailListFilterMenu } from 'owa-mail-list-filter-view';
import { MailListSortMenuV2 } from 'owa-mail-list-sort-view';
import { MailListJumpToPopover, shouldShowJumpTo } from 'owa-mail-list-jumpto-view';
import {
    getStore as getListViewStore,
    getIsSearchTableShown,
    getUserCategoryName,
    type MailFolderTableQuery,
    isCatchUpFilter,
} from 'owa-mail-list-store';
import type { ActionSource } from 'owa-mail-store';
import React from 'react';
import type ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { isFocusedInboxEnabled } from 'owa-mail-triage-common';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import {
    isSingleLineListView,
    isReadingPanePositionOff,
    isReadingPanePositionRight,
} from 'owa-mail-layout';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { getIsNonMailEntitySearchTab } from 'owa-tabbed-search';
import { getMailboxInfoFromTableViewId } from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import { type MailListViewState } from 'owa-mail-store/lib/store/schema/MailListViewState';
import { getMailListLoadState } from 'owa-mail-list-store/lib/utils/getMailListLoadState';
import isSenderImageEnabled from 'owa-sender-image-option/lib/utils/isSenderImageEnabled';
import { isFeatureEnabled } from 'owa-feature-flags';
import { useResizeObserver } from 'owa-react-hooks/lib/useResizeObserver';
import {
    full,
    medium,
    compact,
    SLVItemViewSenderImageOn,
    SLVItemViewSenderImageOff,
    SLVConversationViewSenderImageOn,
    SLVConversationViewSenderImageOff,
    threeColItemViewSenderImageOn,
    threeColItemViewSenderImageOff,
    threeColConversationViewSenderImageOn,
    threeColConversationViewSenderImageOff,
    mailListSecondRowLeftEntity,
    secondHeaderActionContainer,
    selectAllCheckboxContainerSearchTable,
    mailListHeaderContainer,
    mailListHeaderContainerBleedThrough,
    mailListThirdRowMCL,
    mailListThirdRowSCL,
    mailListFirstRow,
    mailListSecondRow,
    mailListSecondRowDenseless,
    mcl,
    scl,
    thirdrowPaddingBottom,
    withFirstRow,
    mailListSecondRowInnerContent,
    mailListSecondRowInnerContentSearchTable,
    notesHeaderSecondRow,
    stickyNotesHeaderPadding,
    mailListSecondRowCustomContainer,
    secondRowCustomContainer,
    selectAllHeaderStyle,
    conversationViewMCL,
    bulkActionBarPadding,
    backButton,
    checkboxSpaceReserved,
    senderImageOn,
    senderImageOff,
    showInboxPivots,
    noInboxPivots,
    headerIconContainer,
    headerIconContainerBleedThrough,
    headerIconContainerFull,
    headerIconContainerMediumOrCompact,
} from './MailListHeader.scss';
import {
    LazyCopilotCatchUpRollup,
    LazyCopilotCatchUpNavigation,
    LazyCopilotCatchUpCard,
} from 'owa-mail-copilot-catchup';
import { isCopilotFeatureEnabled } from 'owa-copilot-settings-utils';
import getCopilotPrioritizeSettings from 'owa-mail-copilot-settings/lib/selectors/getCopilotPrioritizeSettings';
import { isCapabilityEnabled } from 'owa-capabilities';
import { bleedThroughCapability } from 'owa-capabilities-definitions/lib/bleedThroughCapability';
import {
    DEFAULT_LIST_VIEW_WIDTH,
    MIN_LV_WIDTH_WITH_FULL_HEADER_BUTTONS,
} from 'owa-mail-layout/lib/mailLayoutConstants';

import type { UserSettingsStyleSelector } from '../utils/getStyleSelectorAsPerUserSettings';
import classnames from 'owa-classnames';
import SelectAllCheckbox from './SelectAllCheckbox';
import MailListHeaderIconContainer from './MailListHeaderIconContainer';
import exitCopilotCatchUpFilter from '../utils/exitCopilotCatchUpFilter';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import { Button } from '@fluentui/react-components';

export interface MailListHeaderProps {
    filterMenuSource: ActionSource;
    shouldShowFilterMenu: boolean;
    tableViewId: string;
    renderFirstRow?: (mailListRowCss: string) => JSX.Element;
    renderSecondRowCustomContent?: (customContainerCss: string) => JSX.Element;
    renderThirdRow?: (mailListThirdRowCss: string) => JSX.Element;
    renderBulkActionProgressBar?: () => JSX.Element;
    mailListHeaderStylesAsPerUserSettings?: UserSettingsStyleSelector;
}

enum ML_HEADER_BUTTON_STATE {
    FULL, // Full header buttons (e.g. label + icon)
    SEMI_COMPACT, // Semi-compact header buttons (e.g. label + icon for sort button, but no label for all other buttons)
    COMPACT,
}

const arrowIcon = <ArrowLeftRegular />;

//  Header Structure
// ---------------------------------------------------------------------------------------------------
// |  Mail List Header Container                                                                     |
// |  ---------------------------------------------------------------------------------------------  |
// |  | First Row (Custom - renders optionally)                                                   |  |
// |  |                                                                                           |  |
// |  ---------------------------------------------------------------------------------------------  |
// |  ---------------------------------------------------------------------------------------------  |
// |  | Second Row (Half Custom - renders always)                                                 |  |
// |  | ---------- -----------------------------                        --------------------------|  |
// |  | |  Check | |           Custom          |                        |     Filter Dropdown    ||  |
// |  | |   Box  | |           Content         |                        |    (based on scenario) ||  |
// |  | ---------- -----------------------------                        --------------------------|  |
// |  ---------------------------------------------------------------------------------------------  |
// |  ---------------------------------------------------------------------------------------------  |
// |  | Third Row (Custom - renders optionally)                                                   |  |
// |  |                                                                                           |  |
// |  ---------------------------------------------------------------------------------------------  |
// |  ---------------------------------------------------------------------------------------------  |
// |  | Bulk action progress bar (Custom - renders optionally)                                    |  |
// |  |                                                                                           |  |
// |  ---------------------------------------------------------------------------------------------  |
// ---------------------------------------------------------------------------------------------------

export default observer(function MailListHeader(props: MailListHeaderProps) {
    const mailboxInfo = getMailboxInfoFromTableViewId(props.tableViewId);
    const tableView = getListViewStore().tableViews.get(props.tableViewId);
    const isSingleLineView = isSingleLineListView();
    const isItemView = tableView?.tableQuery?.listViewType === 1;
    const isSearchTable = getIsSearchTableShown();
    const isCatchUpFilterSelected = isCatchUpFilter(tableView?.tableQuery as MailFolderTableQuery);
    const showMultiSelectButton = !isSearchTable || isCatchUpFilterSelected;
    const densityModeString = getDensityModeCssClass(full, medium, compact);
    const isSelectionModeActivated = isSingleLineView
        ? tableView?.isInSelectAllStateSLV
        : tableView?.isInSelectAllState;
    const showCheckbox = isSelectionModeActivated || tableView?.isInCheckedMode;
    const isRPDenselessEnabled = isFeatureEnabled('rp-denseless');
    const showCheckboxForReselectFlight =
        isFeatureEnabled('tri-reselect-checkbox-behavior') &&
        (isSingleLineView ||
            isSenderImageEnabled() ||
            (!tableView?.isOnlyOptimisticallyInCheckedMode && showCheckbox) ||
            (tableView?.isOnlyOptimisticallyInCheckedMode &&
                showCheckbox &&
                isSenderImageEnabled()));
    const checkedStateMailList =
        (!isFeatureEnabled('tri-reselect-checkbox-behavior') && showCheckbox) ||
        showCheckboxForReselectFlight;
    const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);
    const mailListViewState = getMailListLoadState();
    const isEmptyFolder = mailListViewState === 'Empty';
    const shouldShowSelectionHeader =
        isReadingPanePositionOff() &&
        ((tableView?.isInCheckedMode && (tableView?.selectedRowKeys.size ?? 0) > 0) ||
            tableView?.isInVirtualSelectAllMode);
    const headerRef = React.useRef<HTMLDivElement>(null);

    const categoryName = tableView ? getUserCategoryName(tableView) : null;
    const isCategoryFolder = categoryName !== null;

    const [mlHeaderButtonState, setMlHeaderButtonState] = React.useState<ML_HEADER_BUTTON_STATE>(
        ML_HEADER_BUTTON_STATE.COMPACT
    );

    useResizeObserver('MailListHeader', headerRef, (bounds: DOMRectReadOnly) => {
        // When in MCL view:
        // If the width is less than DEFAULT_LIST_VIEW_WIDTH (350px) + 100px, we show the compact (icon only) header buttons.
        // If the width is less than MIN_LV_WIDTH_WITH_FULL_HEADER_BUTTONS (625px), we show the semi-compact (some buttons show icon only while others show icon + label) header buttons.
        // Else, we show the full (icon + label) header buttons.
        if (isSingleLineView) {
            if (bounds.width < DEFAULT_LIST_VIEW_WIDTH + 100) {
                setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.COMPACT);
            } else if (bounds.width < MIN_LV_WIDTH_WITH_FULL_HEADER_BUTTONS) {
                setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.SEMI_COMPACT);
            } else {
                setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.FULL);
            }
        } else {
            // When in SCL view:
            // If the width is less than DEFAULT_LIST_VIEW_WIDTH (350px) + 100px, we show the compact (icon only) header buttons.
            // Else, we show the semi-compact (some buttons show icon only while others show icon + label) header buttons.
            if (bounds.width < DEFAULT_LIST_VIEW_WIDTH + 100) {
                setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.COMPACT);
            } else {
                setMlHeaderButtonState(ML_HEADER_BUTTON_STATE.SEMI_COMPACT);
            }
        }
    });

    const alwaysHideLabels =
        isFeatureEnabled('tri-priority-tab-dev2') && isReadingPanePositionRight();

    const showFullLabels = !alwaysHideLabels && mlHeaderButtonState === ML_HEADER_BUTTON_STATE.FULL;
    const showSemiCompactLabels =
        !alwaysHideLabels && mlHeaderButtonState === ML_HEADER_BUTTON_STATE.SEMI_COMPACT;

    const classNameForUserSettings =
        props.mailListHeaderStylesAsPerUserSettings &&
        (
            {
                SLVItemViewSenderImageOn,
                SLVItemViewSenderImageOff,
                SLVConversationViewSenderImageOn,
                SLVConversationViewSenderImageOff,
                threeColItemViewSenderImageOn,
                threeColItemViewSenderImageOff,
                threeColConversationViewSenderImageOn,
                threeColConversationViewSenderImageOff,
            } as const
        )[props.mailListHeaderStylesAsPerUserSettings];

    const onExitCopilotCatchUpFilterClick = React.useCallback(() => {
        if (tableView) {
            exitCopilotCatchUpFilter(tableView);
        }
    }, [tableView]);

    const renderSecondRowLeftEntity = (
        isInboxWithPivots: boolean,
        isStickyNotesHeader: boolean
    ) => {
        const shouldShowSelectAllCheckbox =
            showCheckboxForReselectFlight || !isFeatureEnabled('tri-reselect-checkbox-behavior');
        // Notes has no checkbox, while non-mail search tabs require a placeholder to avoid jumpy UI
        if (isStickyNotesHeader) {
            return null;
        } else if (getIsNonMailEntitySearchTab()) {
            return <div className={mailListSecondRowLeftEntity} />;
        } else if (!isEmptyFolder && shouldShowSelectAllCheckbox) {
            return (
                <div
                    className={classnames(
                        secondHeaderActionContainer,
                        isSingleLineView ? mcl : scl,
                        isInboxWithPivots && !shouldShowSelectionHeader
                            ? showInboxPivots
                            : noInboxPivots,
                        densityModeString,
                        isSearchTable && selectAllCheckboxContainerSearchTable
                    )}
                >
                    <SelectAllCheckbox
                        tableViewId={props.tableViewId}
                        isInboxWithPivots={isInboxWithPivots}
                    />
                </div>
            );
        } else {
            if (isSearchTable) {
                return (
                    <div
                        className={classnames(
                            secondHeaderActionContainer,
                            isSingleLineView ? mcl : scl,
                            isInboxWithPivots && !shouldShowSelectionHeader
                                ? showInboxPivots
                                : noInboxPivots,
                            densityModeString
                        )}
                    />
                );
            } else {
                return;
            }
        }
    };

    const renderMailListFilter = () => {
        const { filterMenuSource, tableViewId } = props;
        const supportedViewFilters = getViewFilters();
        return (
            <MailListFilterMenu
                filterMenuSource={filterMenuSource}
                supportedViewFilters={supportedViewFilters}
                tableViewId={tableViewId}
                showButtonLabel={showFullLabels}
                isBleedThrough={isRPDenselessEnabled}
            />
        );
    };

    const { prioritizationEnabled, experimentalSetting } =
        getCopilotPrioritizeSettings(mailboxInfo);
    const shouldShowPriorityTab =
        isFeatureEnabled('tri-priority-tab-dev2') &&
        isCopilotFeatureEnabled('Inbox', mailboxInfo, true /* skipLanguageCheck */) &&
        prioritizationEnabled &&
        experimentalSetting !== 'false';

    const mailListThirdRowCss = classnames(
        isSingleLineView ? mailListThirdRowMCL : mailListThirdRowSCL,
        classNameForUserSettings
    );
    const folderId = tableView?.tableQuery?.folderId ?? '';
    const isInboxWithPivots =
        (isFocusedInboxEnabled(mailboxInfo) || !!shouldShowPriorityTab) &&
        'inbox' === folderIdToName(folderId);
    const isStickyNotesHeader =
        folderIdToName(folderId) === 'notes' && isFeatureEnabled('notes-folder-view');
    const isLegacyNotesHeader =
        folderIdToName(folderId) === 'notes' && !isFeatureEnabled('notes-folder-view');
    if (!props.renderSecondRowCustomContent) {
        throw new Error('MailListHeader: renderSecondRowCustomContent should not be null');
    }
    const thirdRow = props.renderThirdRow?.(mailListThirdRowCss);
    const firstRow = props.renderFirstRow?.(mailListFirstRow);
    const isCheckboxSpaceReserved =
        isSearchTable || checkedStateMailList || isCategoryFolder || isLegacyNotesHeader;
    return (
        <div
            className={classnames(
                mailListHeaderContainer,
                isRPDenselessEnabled && isBleedThroughEnabled && mailListHeaderContainerBleedThrough
            )}
            ref={headerRef}
        >
            {props.renderFirstRow?.(mailListFirstRow)}
            <div
                className={classnames(
                    mailListSecondRow,
                    isRPDenselessEnabled && mailListSecondRowDenseless,
                    isSingleLineView ? mcl : scl,
                    isSenderImageEnabled() ? senderImageOn : senderImageOff,
                    isCheckboxSpaceReserved && checkboxSpaceReserved,
                    isInboxWithPivots ? showInboxPivots : noInboxPivots,
                    isStickyNotesHeader && stickyNotesHeaderPadding,
                    thirdRow && thirdrowPaddingBottom,
                    firstRow && withFirstRow,
                    densityModeString
                )}
            >
                <div
                    className={classnames(
                        mailListSecondRowInnerContent,
                        isSearchTable && mailListSecondRowInnerContentSearchTable
                    )}
                >
                    {!isCatchUpFilterSelected &&
                        ((showMultiSelectButton &&
                            (checkedStateMailList || !props.shouldShowFilterMenu)) ||
                            showCheckboxForReselectFlight ||
                            !showMultiSelectButton) &&
                        renderSecondRowLeftEntity(isInboxWithPivots, isStickyNotesHeader)}
                    {isCatchUpFilterSelected && (
                        <div
                            className={classnames(
                                secondHeaderActionContainer,
                                isSingleLineView ? mcl : scl,
                                noInboxPivots,
                                densityModeString
                            )}
                        >
                            <Button
                                icon={arrowIcon}
                                appearance="transparent"
                                size="small"
                                onClick={onExitCopilotCatchUpFilterClick}
                                className={backButton}
                            />
                        </div>
                    )}
                    {((showMultiSelectButton && !checkedStateMailList) ||
                        isEmptyFolder ||
                        !showMultiSelectButton) &&
                        props.renderSecondRowCustomContent(
                            classnames(
                                isStickyNotesHeader
                                    ? notesHeaderSecondRow
                                    : mailListSecondRowCustomContainer,
                                classNameForUserSettings,
                                secondRowCustomContainer,
                                densityModeString
                            )
                        )}
                    {showMultiSelectButton &&
                        checkedStateMailList &&
                        !isEmptyFolder &&
                        props.renderSecondRowCustomContent(
                            classnames(
                                notesHeaderSecondRow,
                                selectAllHeaderStyle,
                                densityModeString
                            )
                        )}
                    {showMultiSelectButton &&
                        props.shouldShowFilterMenu &&
                        !isEmptyFolder &&
                        !isCatchUpFilterSelected && (
                            <MailListHeaderIconContainer
                                tableView={tableView}
                                supportedViewFilters={getViewFilters()}
                                filterMenuSource={props.filterMenuSource}
                                tableViewId={props.tableViewId}
                                showFullLabels={showFullLabels}
                                showSemiCompactLabels={showSemiCompactLabels}
                                isBleedThrough={isRPDenselessEnabled}
                            />
                        )}
                    {(!showMultiSelectButton || isEmptyFolder) &&
                        !isCatchUpFilterSelected &&
                        props.shouldShowFilterMenu &&
                        props.filterMenuSource !== 'GroupHeader' && (
                            <div
                                className={classnames(
                                    headerIconContainer,
                                    isRPDenselessEnabled && headerIconContainerBleedThrough,
                                    getDensityModeCssClass(
                                        headerIconContainerFull,
                                        headerIconContainerMediumOrCompact,
                                        headerIconContainerMediumOrCompact
                                    )
                                )}
                            >
                                {shouldShowJumpTo(tableView) && (
                                    <MailListJumpToPopover
                                        tableViewId={props.tableViewId}
                                        showButtonLabel={showFullLabels}
                                        isBleedThrough={isRPDenselessEnabled}
                                    />
                                )}
                                {renderMailListFilter()}
                                <MailListSortMenuV2
                                    sortMenuSource={props.filterMenuSource}
                                    tableViewId={props.tableViewId}
                                    showButtonLabel={showSemiCompactLabels || showFullLabels}
                                    isBleedThrough={isRPDenselessEnabled}
                                />
                            </div>
                        )}
                    {isCatchUpFilterSelected && tableView && (
                        <div
                            className={classnames(
                                headerIconContainer,
                                isRPDenselessEnabled && headerIconContainerBleedThrough,
                                getDensityModeCssClass(
                                    headerIconContainerFull,
                                    headerIconContainerMediumOrCompact,
                                    headerIconContainerMediumOrCompact
                                )
                            )}
                        >
                            <LazyCopilotCatchUpNavigation tableView={tableView} />
                        </div>
                    )}
                </div>
            </div>
            {!isCatchUpFilterSelected && (
                <LazyCopilotCatchUpRollup
                    key="copilotCatchUpRollup_0"
                    tableViewId={props.tableViewId}
                />
            )}
            {isCatchUpFilterSelected && tableView && (
                <LazyCopilotCatchUpCard tableView={tableView} />
            )}
            {thirdRow}
            <div
                className={classnames(
                    bulkActionBarPadding,
                    classNameForUserSettings,
                    isSingleLineView && !isItemView && conversationViewMCL
                )}
            >
                {props.renderBulkActionProgressBar?.()}
            </div>
        </div>
    );
}, 'MailListHeader');

function getViewFilters(): ViewFilter[] {
    const viewFilters: ViewFilter[] = [
        'All',
        'Unread',
        'Flagged',
        'ToOrCcMe',
        'HasFile',
        'Mentioned',
        'HasCalendarInvite',
    ];
    return viewFilters;
}
